import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Inbox, Loader2, Mail, MessageCircle } from "lucide-react";
import AdminShell, { Pending } from "@/components/admin/AdminShell";
import { Card, Empty, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUSES = ["all", "new", "contacted", "closed"];
const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const Transcript = ({ id }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/chat-leads/${enc(id)}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setD({ messages: [] }); }); }, [id]);
  if (!d) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#D4AF37]" />;
  return (
    <div className="flex max-h-96 flex-col gap-2 overflow-y-auto" data-testid={`chat-lead-transcript-${id}`}>
      {(d.messages || []).map((m, i) => (
        <div key={i} className={cn("max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm", m.role === "user" ? "self-end bg-[#D4AF37]/15 text-zinc-100" : "self-start border border-[#27272A] bg-[#18181B] text-zinc-300")}>
          <div className="mb-0.5 text-[10px] text-zinc-500">{m.role === "user" ? "Visitor" : "Assistant"} · {fmt(m.created_at)}</div>{m.content}
        </div>
      ))}
      {!d.messages?.length && <p className="text-xs text-zinc-500">No messages stored.</p>}
    </div>
  );
};

const Row = ({ r, open, onToggle, onStatus }) => (
  <div className="rounded-xl border border-[#27272A] bg-[#09090B] p-4" data-testid={`chat-lead-row-${r.session_id}`}>
    <div className="flex flex-col gap-3 md:flex-row md:items-center">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37]"><MessageCircle className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm"><span className="font-medium text-zinc-100">{r.name || "Unknown visitor"}</span>{r.email && <a href={`mailto:${r.email}`} className="text-xs text-sky-300 hover:underline">{r.email}</a>}{r.phone && <span className="text-xs text-zinc-500">{r.phone}</span>}</div>
        {(r.summary || r.topic) && <p className="mt-0.5 text-xs text-zinc-400">{r.summary || r.topic}</p>}
        <p className="mt-1 text-[11px] text-zinc-600">{fmt(r.handed_off_at || r.updated_at || r.created_at)}{r.page ? ` · from ${r.page}` : ""}{r.user_id ? " · signed-in user" : ""}{r.message_count ? ` · ${r.message_count} messages` : ""}</p>
        {r.note && <p className="mt-1 text-xs text-zinc-300">Note: {r.note}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {r.email && <a href={`mailto:${r.email}?subject=${encodeURIComponent("Re: your RPOPRO chat")}`} className="inline-flex h-9 items-center gap-1 rounded-md bg-[#D4AF37] px-3 text-xs font-semibold text-black" data-testid={`chat-lead-email-${r.session_id}`}><Mail className="h-3.5 w-3.5" />Email</a>}
        <button onClick={onToggle} className="inline-flex h-9 items-center gap-1 rounded-md border border-[#27272A] px-3 text-xs text-zinc-200" data-testid={`chat-lead-toggle-${r.session_id}`}>Chat<ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} /></button>
        <select value={r.status || "new"} onChange={(e) => onStatus(r, e.target.value)} className="h-9 rounded-md border border-[#27272A] bg-[#18181B] px-2 text-xs capitalize text-zinc-200" data-testid={`chat-lead-status-${r.session_id}`}>
          {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
    {open && <div className="mt-4 border-t border-[#27272A] pt-4"><Transcript id={r.session_id} /></div>}
  </div>
);

export default function ChatLeads() {
  const [items, setItems] = useState(null);
  const [missing, setMissing] = useState(false);
  const [status, setStatus] = useState("all");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/admin/chat-leads").then((r) => setItems(r.data?.items || [])).catch((e) => { if (e.response?.status === 404) setMissing(true); else toast.error(errMsg(e)); setItems([]); });
  }, []);

  const setRowStatus = async (r, s) => {
    const prev = items;
    setItems((x) => x.map((i) => (i.session_id === r.session_id ? { ...i, status: s } : i)));
    try { await api.patch(`/admin/chat-leads/${enc(r.session_id)}`, { status: s }); } catch (e) { setItems(prev); toast.error(errMsg(e)); }
  };

  const rows = (items || []).filter((r) => status === "all" || (r.status || "new") === status);
  return (
    <AdminShell title="Chat Leads" subtitle="Website chats the assistant handed to you. Each one is also emailed to tina@rpopro.co.">
      {missing && <Pending />}
      <Card className="mb-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`chat-leads-status-${s}`} className={cn("rounded-full border px-3 py-1 text-xs capitalize", status === s ? "border-[#D4AF37] bg-[#D4AF37] font-semibold text-black" : "border-[#27272A] text-zinc-400")}>{s}</button>)}
      </Card>
      {!items ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : rows.length === 0 ? <Empty icon={Inbox} title="No chat leads yet" text="When a visitor asks for a person, their chat shows up here." /> : (
        <div className="space-y-2" data-testid="chat-leads-list">{rows.map((r) => <Row key={r.session_id} r={r} open={openId === r.session_id} onToggle={() => setOpenId((o) => (o === r.session_id ? null : r.session_id))} onStatus={setRowStatus} />)}</div>
      )}
    </AdminShell>
  );
}
